import React, { useMemo, useState } from "react";
import ClusterKeywordRows from "./ClusterKeywordRows";
import TrendSparkline from "./TrendSparkline";
import useExpandableRows from "../useExpandableRows";
import { formatMetric, formatNumber } from "./clusterFormat";
import { trendStyle } from "./seoTrends";

// Sort options are explicit rather than "click any header" because only a few
// orderings actually answer a question. "Biggest drop" is the one that matters
// most — a declining cluster is the thing you want to find first.
const SORTS = [
  { id: "movement", label: "Biggest movement" },
  { id: "impressions", label: "Most impressions" },
  { id: "position", label: "Best position" },
  { id: "declining", label: "Biggest drop" },
];

/** Position renders as an em dash when null — "never ranked" is not rank 0. */
function PositionCell({ before, after, trend }) {
  const { className, arrow } = trendStyle(trend);
  return (
    <span className={className}>
      {formatMetric(before)} → {formatMetric(after)} {arrow}
    </span>
  );
}

function sortRows(rows, sort) {
  const copy = [...rows];
  switch (sort) {
    case "impressions":
      return copy.sort((a, b) => b.impressionsAfter - a.impressionsAfter);
    case "position":
      // Nulls sort last: a cluster that never ranked is not "best".
      return copy.sort((a, b) => {
        if (a.positionAfter === null) return 1;
        if (b.positionAfter === null) return -1;
        return a.positionAfter - b.positionAfter;
      });
    case "declining":
      return copy.sort((a, b) => {
        const rank = (r) => (r.positionTrend === "lost" ? Infinity : r.positionDelta ?? -Infinity);
        return rank(b) - rank(a);
      });
    case "movement":
    default:
      return copy.sort(
        (a, b) => Math.abs(b.impressionsDelta || 0) - Math.abs(a.impressionsDelta || 0)
      );
  }
}

/** Header cells; the leading blank column holds the expand chevron. */
const COLUMNS = ["", "Cluster", "Clicks", "Impressions", "Position", "Impressions trend"];

const ClusterTrendTable = ({ clusters, fromDate, toDate }) => {
  const [sort, setSort] = useState("movement");
  const { toggleRow, isExpanded } = useExpandableRows();
  const rows = useMemo(() => sortRows(clusters || [], sort), [clusters, sort]);

  if (!rows.length) {
    return <p className="text-sm text-gray-500 italic">No cluster data in this range.</p>;
  }

  return (
    <div>
      <div className="flex gap-2 mb-3">
        {SORTS.map((option) => (
          <button
            key={option.id}
            onClick={() => setSort(option.id)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              sort === option.id
                ? "bg-gray-800 text-white border-gray-800"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
              {COLUMNS.map((label, i) => (
                <th
                  key={label || "expand"}
                  className={`py-2 pr-4 font-medium ${i === 2 || i === 3 ? "text-right" : ""} ${
                    i === 0 ? "w-6" : ""
                  }`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const impressions = trendStyle(row.impressionsTrend);
              const expanded = isExpanded(row.id);
              return (
                <React.Fragment key={row.id}>
                <tr
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleRow(row.id)}
                >
                  <td className="py-2 pr-1 text-gray-400 select-none">
                    {/* Rotating a single glyph avoids shipping an icon set. */}
                    <span
                      className="inline-block transition-transform"
                      style={{ transform: expanded ? "rotate(90deg)" : "none" }}
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </td>
                  <td className="py-2 pr-4 font-medium text-gray-800">{row.id}</td>
                  <td className="py-2 pr-4 text-right text-gray-700">
                    {formatNumber(row.clicksBefore)} → {formatNumber(row.clicksAfter)}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-700">
                    {formatNumber(row.impressionsBefore)} → {formatNumber(row.impressionsAfter)}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <PositionCell
                      before={row.positionBefore}
                      after={row.positionAfter}
                      trend={row.positionTrend}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <TrendSparkline
                        data={(row.history || []).map((h) => h.impressions)}
                        color={row.impressionsTrend === "declining" ? "#dc2626" : "#059669"}
                      />
                      <span className={`text-xs ${impressions.className}`}>{impressions.arrow}</span>
                    </div>
                  </td>
                </tr>

                {/* Mounted only when open, so keyword rows are fetched lazily
                    — collapsed clusters cost nothing. */}
                {expanded && (
                  <ClusterKeywordRows
                    clusterId={row.id}
                    fromDate={fromDate}
                    toDate={toDate}
                    colSpan={COLUMNS.length}
                  />
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClusterTrendTable;
